import create from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";

import * as api2 from "js/api/api";
import * as authApi from "js/api/authApi";
import { getUserDetail } from "js/api/usersApi";
// import { initLogRocket } from "js/utils/logRocketUtils";
import { invalidateTokenOnServer } from "js/api/logoutApi";
import { initUserFlow, resetUserflow } from "utils/userflowUtils";

import {
	ApiError,
	DeviceInfo,
	DeviceStats,
	LoginDetails,
	LoginFailure,
	LoginResponse,
	ServerError,
	StandardLoginDetails,
	UserInfo,
} from "js/api/authApi";
import { initGoogleTagManager } from "utils/gtmUtils";
import { initPostHog, resetPosthog } from "@/utils/posthogUtils";
import { AcknowledgedRedirectKey } from "@/js/components/Login";

export interface UserSessionState {
	loaded: boolean;
	authenticated: boolean;
	active_group: string | undefined;
	active_user_token: string | undefined;
	active_username: string | undefined;
	active_device: string | undefined;
	last_login_failed: boolean;
	last_login_failed_message: string;
	device_stats: DeviceStats | undefined;
	chat_enable_box: string;
	userinfo: UserInfo | undefined;
	deviceinfo: DeviceInfo | undefined;
	deviceid: string | undefined;
	teacher_classrooms_enabled?: boolean;
	customer_internal_company: string;
}

interface Actions {
	setActiveClassroom: (group: string) => void;

	authenticate: (credentials: StandardLoginDetails) => Promise<void>;
	authenticateMasquerade: (credentials: LoginDetails) => Promise<void>;
	handleAuthenticationSuccess: (data: LoginResponse, masquerade: boolean) => Promise<void>;
	handleAuthenticationFailure: (error: LoginFailure | ApiError<ServerError>) => void;

	logout: () => Promise<void>;
	handleInvalidTokenError: () => void;
	clearSession: () => void;

	neverShowChatEnableModal: () => void;
	fetchUserInfo: () => Promise<void>;
	fetchDeviceInfo: () => Promise<void>;

	isAuthenticated: () => boolean;
	getActiveGroup: () => string | undefined;
	getUsersGroups: () => string[];
	getTeacherEmail: () => string | undefined;
	getToken: () => string | undefined;
	getActiveUser: () => string | undefined;
	getActiveDevice: () => string | undefined;
	failedLogin: () => boolean;
	failedLoginReason: () => string;
	hasLoaded: () => boolean;
	getNeverShowChatEnable: () => string;
	getDeviceStats: () => Promise<DeviceStats | Record<string, unknown>>;
	/**
	 * Get the teacher's (stable) ID
	 */
	getTeacherId: () => string | undefined;
}

export function clearClasswizeLocalStorage(): void {
	const keysToPreserve = ["CHAT_NEVER_SHOW", AcknowledgedRedirectKey];
	for (const key in localStorage) {
		if (!keysToPreserve.includes(key)) {
			localStorage.removeItem(key);
		}
	}
}

export const useUserSessionStore = create(
	devtools(
		subscribeWithSelector<UserSessionState & Actions>((set, get) => ({
			loaded: false,
			authenticated: !!localStorage.getItem("TOKEN"),
			active_group: localStorage.getItem("ACTIVE_GROUP") as string,
			active_user_token: localStorage.getItem("TOKEN") as string,
			active_username: localStorage.getItem("USERNAME") as string,
			active_device: localStorage.getItem("DEVICE") as string,
			last_login_failed: false,
			last_login_failed_message: "",
			device_stats: undefined,
			chat_enable_box: localStorage.getItem("CHAT_NEVER_SHOW") as string,
			userinfo: undefined,
			deviceinfo: undefined,
			deviceid: "",
			customer_internal_company: "",

			setActiveClassroom: (group: string) => {
				set(() => ({
					active_group: group,
				}));

				// "ACTIVE_GROUP" is still used by chat.
				localStorage.setItem("ACTIVE_GROUP", group);
			},

			authenticate: async (credentials: StandardLoginDetails) => {
				set(() => ({
					last_login_failed: false,
					last_login_failed_message: "",
					deviceid: undefined,
					active_user_token: undefined,
					active_device: undefined,
					active_username: undefined,
					teacher_classrooms_enabled: false,
				}));

				clearClasswizeLocalStorage();

				if (credentials.token !== undefined) {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore: Ignoring for now
					await get().handleAuthenticationSuccess(credentials, false);
				} else {
					try {
						const res = await authApi.loginStandard(credentials);
						localStorage.setItem("DEVICE", res.deviceid);
						await get().handleAuthenticationSuccess(res, false);
					} catch (error) {
						get().handleAuthenticationFailure(error as ApiError<ServerError>);
					}
				}
			},

			authenticateMasquerade: async (credentials: LoginDetails) => {
				set(() => ({
					last_login_failed: false,
					last_login_failed_message: "",
					deviceid: undefined,
					active_user_token: undefined,
					active_device: undefined,
					active_username: undefined,
					teacher_classrooms_enabled: false,
				}));
				clearClasswizeLocalStorage();

				try {
					const res = await authApi.loginMasquerade(credentials);
					await get().handleAuthenticationSuccess(res, true);
				} catch (error) {
					get().handleAuthenticationFailure(error as ApiError<ServerError>);
				}
			},

			handleAuthenticationSuccess: async (data: LoginResponse, masquerade: boolean) => {
				localStorage.setItem("TOKEN", data.token);
				localStorage.setItem("USERNAME", data.username);
				localStorage.setItem("DEVICE", data.deviceid);

				set(() => ({
					deviceid: data.deviceid,
					active_user_token: data.token,
					active_device: data.deviceid,
					active_username: data.username,
					last_login_failed: false,
					last_login_failed_message: "",
					authenticated: true,
					loaded: true,
				}));

				await Promise.all([get().fetchDeviceInfo(), get().fetchUserInfo()]);

				// if this isn't a masquerade login, initialise user analytics services
				if (!masquerade) {
					const state: UserSessionState = get();

					if (state.deviceinfo && state.userinfo) {
						if (state.deviceinfo.region !== "beta-1" && state.deviceinfo.region !== "sit") {
							initPostHog(
								state.active_username as string,
								state.userinfo.email,
								state.active_device as string,
								state.deviceinfo.user_defined_name
							);
						}
					}

					if (state.deviceinfo) {
						initGoogleTagManager(state.deviceinfo.region);
					} else {
						console.error("appliance info is missing, skipping Google Tag Manager init");
					}

					if (state.deviceinfo && state.userinfo) {
						initUserFlow(
							state.active_username as string,
							state.userinfo.email,
							state.userinfo.firstName,
							state.userinfo.lastName,
							state.deviceinfo.user_defined_name,
							state.active_device as string,
							state.deviceinfo.timezone,
							state.deviceinfo.region,
							state.active_group as string,
							data.userflowToken,
							data.userflowUserIdentity,
							data.userflowCompanyIdentity
						);
					} else {
						console.error("appliance or user info is missing, skipping UserFlow init");
					}

					// initLogRocket(data.deviceid, data.username);
				}
			},

			handleAuthenticationFailure: (error: LoginFailure) => {
				set(() => ({
					last_login_failed: true,
					last_login_failed_message: error.error,
				}));
			},

			logout: async () => {
				if (get().active_user_token) {
					await invalidateTokenOnServer();
				}
				// The above may fail if the token is already invalid. So we also clear the session separately here.
				get().clearSession();
			},

			handleInvalidTokenError: () => {
				// Session is already invalid. No need to invalidate token again. Just clear the session.
				get().clearSession();
			},

			clearSession: () => {
				clearClasswizeLocalStorage();

				// Set the Appliance ID to be auto-selected for next login
				localStorage.setItem("LOGIN_DEVICEID", get().active_device as string);

				set(() => ({
					active_group: undefined,
					active_user_token: undefined,
					active_username: undefined,
					active_device: undefined,
					userinfo: undefined,
					deviceinfo: undefined,
					authenticated: false,
					device_stats: undefined,
				}));

				resetUserflow();
				resetPosthog();
			},

			neverShowChatEnableModal: () => {
				localStorage.setItem("CHAT_NEVER_SHOW", true.toString());
				set(() => ({
					chat_enable_box: true.toString(),
				}));
			},

			fetchUserInfo: async () => {
				const userInfo = (await getUserDetail(get().active_username ?? "", true, false, false)) as UserInfo;
				set(() => ({
					userinfo: userInfo,
				}));
			},

			fetchDeviceInfo: async () => {
				return api2.get("/deviceinfo").then((data: DeviceInfo) => {
					set(() => ({
						deviceinfo: data,
					}));
				});
			},

			isAuthenticated: () => {
				return get().authenticated;
			},
			getActiveGroup: () => {
				return get().active_group;
			},
			getUsersGroups: () => {
				const { userinfo } = get();
				if (!userinfo || !Object.keys(userinfo).length) {
					return [];
				}
				const teacherGroups: string[] = [];
				if (userinfo.groups) {
					teacherGroups.push(...userinfo.groups.map((g) => g.name));
				}
				if (userinfo.groupsOwned) {
					teacherGroups.push(...userinfo.groupsOwned.map((g) => g.name));
				}
				return teacherGroups;
			},
			getTeacherEmail: () => {
				return get().userinfo?.email;
			},
			getToken: () => {
				return get().active_user_token;
			},
			getActiveUser: () => {
				return get().active_username;
			},
			getActiveDevice: () => {
				return get().active_device;
			},
			failedLogin: () => {
				return get().last_login_failed;
			},
			failedLoginReason: () => {
				return get().last_login_failed_message;
			},
			hasLoaded: () => {
				return get().loaded;
			},
			getNeverShowChatEnable: () => {
				return get().chat_enable_box;
			},
			getDeviceStats: async () => {
				const { device_stats } = get();
				if (device_stats) {
					return device_stats;
				}
				try {
					const { result } = (await api2.get("/device-stats")) as { result: DeviceStats };
					set(() => ({
						device_stats: result,
					}));

					return result;
				} catch (err) {
					console.log("Error fetching device stats:", err);
					return {};
				}
			},

			/**
			 * Get the teacher Id
			 * @returns logged in teacher's stableId
			 */
			getTeacherId: () => {
				return get().userinfo?.stableIdV1;
			},
		}))
	)
);
